import axios from 'axios';
import { datadogRum } from '@datadog/browser-rum';

import { setLocalStorage, getDecodedToken, isTokenExpired } from './utils';

import Token from 'modules/Auth/common/Token.interface';
import { localViews } from 'modules/Auth/AuthProvider/views';
import config from 'modules/common/config';
import { sendDatadogRumError } from 'utils/analytics/datadogRum';

/**
 * Receives the refreshed token, which is always a complete `Token`: `refreshAccessToken` returns
 * early unless the stored token has a `refresh` field, only proceeds when the response carries an
 * `access`, and merges the two — so consumers can dispatch it as auth state without re-validating.
 */
export type TokenRefreshedCallback = ((token: Token) => void) | null;
export type RefreshAccessTokenFn = () => Promise<boolean>;
export type SetOnTokenRefreshedFn = (callback: TokenRefreshedCallback) => void;
export type IsAccessTokenExpiringSoonFn = (thresholdSeconds?: number) => boolean;

let isRefreshing = false;
let refreshPromise: Promise<boolean> | null = null;

// Callback to sync refreshed token into AuthContext (registered by App component)
let onTokenRefreshed: TokenRefreshedCallback = null;

export function setOnTokenRefreshed(callback: TokenRefreshedCallback) {
    onTokenRefreshed = callback;
}

/**
 * Standalone token refresh function that can be called from the axios interceptor.
 * Handles concurrent refresh calls by returning a shared promise.
 */
export async function refreshAccessToken(): Promise<boolean> {
    // Return existing promise if refresh is in progress
    if (isRefreshing && refreshPromise) {
        return refreshPromise;
    }

    const token = localViews.token();
    const refresh = token?.refresh;

    // Can't refresh without a refresh token
    if (!refresh) {
        return false;
    }

    // Check if refresh token itself is expired
    try {
        const decodedRefresh = getDecodedToken(refresh);
        if (isTokenExpired(decodedRefresh)) {
            datadogRum.addAction('Refresh token expired', {
                type: 'TOKEN_REFRESH_SKIPPED',
                reason: 'refresh_token_expired',
                expiresAt: decodedRefresh.exp,
            });
            return false;
        }
    } catch (error) {
        sendDatadogRumError('Failed to decode refresh token', error, {
            type: 'TOKEN_DECODE_ERROR',
        });
        return false;
    }

    isRefreshing = true;
    refreshPromise = (async () => {
        try {
            // Use raw axios to avoid interceptor loop
            const { data } = await axios.post(
                `${config.djangoURL}rest-auth/refresh-token/`,
                { refresh },
                {
                    headers: { 'Content-Type': 'application/json' },
                    withCredentials: true,
                    timeout: 10000,
                }
            );

            if (data?.access) {
                // Merge with existing token to preserve refresh token and other fields
                const currentToken = localViews.token();
                const mergedToken = { ...currentToken, ...data };
                // Write to localStorage directly as a safety net in case onTokenRefreshed
                // callback is not yet registered. The callback also triggers a reducer
                // dispatch that writes to localStorage, but this ensures the token is
                // persisted even without an active React context.
                setLocalStorage('token', mergedToken);
                try {
                    onTokenRefreshed?.(mergedToken);
                } catch (callbackError) {
                    sendDatadogRumError('onTokenRefreshed callback failed', callbackError, {
                        type: 'TOKEN_REFRESH_CALLBACK_ERROR',
                    });
                }
                return true;
            }
            return false;
        } catch (error) {
            sendDatadogRumError('Token refresh failed', error, {
                type: 'ERROR_TOKEN_REFRESH',
            });
            return false;
        } finally {
            isRefreshing = false;
            refreshPromise = null;
        }
    })();

    return refreshPromise;
}

/**
 * Checks if the access token is expiring within the given threshold.
 * @param thresholdSeconds - Number of seconds before expiry to consider "expiring soon"
 */
export function isAccessTokenExpiringSoon(thresholdSeconds = 60): boolean {
    const token = localViews.token()?.access;
    if (!token) {
        return false;
    }

    try {
        const decoded = getDecodedToken(token);
        const now = Math.floor(Date.now() / 1000);
        return decoded.exp - now < thresholdSeconds;
    } catch {
        return false;
    }
}
